import { useContext, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppContext } from 'app/frontend/base/config/context/app'

import {
  ChevronDown,
  FileText,
  LogOut,
  Monitor,
  Moon,
  Star,
  Sun,
  UserRoundCog,
  UsersRound,
  VenetianMask,
} from 'lucide-react'

import { useTheme } from '@/components/theme-provider'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Button, buttonVariants } from '@/components/ui/button'
import CharacterAvatar from 'app/frontend/base/components/CharacterAvatar'
import {
  usePageInfo,
  useUserApplicationSummary,
} from 'app/frontend/base/config/queries'
import { useHasSidebar, useSubscription } from 'app/frontend/base/helpers'
import PremiumModal from 'app/frontend/base/components/PremiumModal'
import { toast } from 'sonner'
import { deleteRequest } from 'app/frontend/base/api'
import CharacterSwitcher from 'app/frontend/base/components/CharacterSwitcher'
import { urlToMainSite } from '../../TeamDashboard/subpages/TeamApplicants/utils'
import NumberIndicator from 'app/frontend/base/components/NumberIndicator'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'

const HeaderDropdown = ({
  hideCharacterContext = false,
}: {
  hideCharacterContext?: boolean
}) => {
  const { t } = useTranslation()
  const { customDomain } = useCustomDomain()
  const {
    data: { user, impersonating },
  } = usePageInfo()
  const { currentCharacterRef } = useContext(AppContext)
  const { data: applicationSummary } = useUserApplicationSummary()
  const [switchingCharacter, setSwitchingCharacter] = useState(false)
  const hasSidebar = useHasSidebar()
  const { theme, setTheme } = useTheme()
  const { subscriptionColor, subscriptionName, subscriptionActive } =
    useSubscription(user)

  const unreadApplicationMessages = useMemo(() => {
    return Object.fromEntries(
      Object.entries(applicationSummary ?? {}).filter(
        ([_, count]) => count > 0,
      ),
    )
  }, [applicationSummary])

  const stopImpersonation = async () => {
    try {
      await deleteRequest('/admin/impersonations/delete')
      window.location.reload()
    } catch {
      toast.error('Something went wrong')
    }
  }

  if (!user) return null

  const unreadIndicator =
    Object.keys(unreadApplicationMessages).length > 0 ? (
      <NumberIndicator
        number={Object.values(unreadApplicationMessages).reduce(
          (acc, count) => acc + count,
          0,
        )}
      />
    ) : null

  const hasFixedTheme =
    customDomain?.themeMode &&
    ['dark', 'light'].includes(customDomain?.themeMode)

  return (
    <>
      <CharacterSwitcher
        open={switchingCharacter}
        setOpen={setSwitchingCharacter}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span className='flex gap-1 items-center cursor-pointer'>
            {!hideCharacterContext ? (
              <span className='relative'>
                <CharacterAvatar
                  className={`mr-1.5 h-10 w-10 min-w-10 min-h-10`}
                  character={currentCharacterRef}
                />
                <span className='absolute top-[-0.2rem] right-[-0.3rem]'>
                  {unreadIndicator}
                </span>
              </span>
            ) : null}

            <span
              className={`text-base hidden sm:flex ${
                impersonating
                  ? "bg-info-foreground min-w-28 h-10 px-2 py-0.5 justify-center items-end relative rounded-md text-black before:top-0 before:right-0.5 before:w-full before:flex before:justify-center before:absolute before:text-xs before:opacity-70 before:content-['impersonating']"
                  : ''
              }`}
            >
              {currentCharacterRef?.name || user.battletag}
            </span>

            {subscriptionActive ? (
              <Star width={16} className={`ml-1 ${subscriptionColor.text}`} />
            ) : null}

            <ChevronDown
              width={12}
              className='hidden sm:block ml-0.5 mt-1 opacity-70'
            />

            {hideCharacterContext ? unreadIndicator : null}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' side={hasSidebar ? 'right' : 'bottom'}>
          <DropdownMenuLabel>
            {currentCharacterRef ? (
              <>
                <span className='flex items-center justify-between relative'>
                  <span className='min-w-30'>
                    <Link to={currentCharacterRef.path}>
                      {currentCharacterRef.name}
                    </Link>
                  </span>
                  <span className='opacity-70 text-[0.7rem] mr-0.5 font-normal'>
                    {user.battletag}
                  </span>
                </span>
                <div className='opacity-70 font-normal text-[0.7rem] mt-[-0.2rem]'>
                  {currentCharacterRef.realm.name} (
                  <span>{currentCharacterRef.realm.region}</span>)
                </div>
              </>
            ) : (
              <span>{user.battletag}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {hideCharacterContext ? null : (
            <DropdownMenuItem onClick={() => setSwitchingCharacter(true)}>
              <span className='flex gap-2 items-center'>
                <UsersRound width={14} />
                {currentCharacterRef
                  ? t('header.switch-character')
                  : t('header.set-character')}
              </span>
            </DropdownMenuItem>
          )}

          <Link
            to={hideCharacterContext ? urlToMainSite('/profile') : '/profile'}
          >
            <DropdownMenuItem className='gap-2'>
              <UserRoundCog width={14} />
              {t('header.profile')}
            </DropdownMenuItem>
          </Link>

          <Link
            to={
              hideCharacterContext
                ? urlToMainSite('/profile/applications')
                : '/profile/applications'
            }
          >
            <DropdownMenuItem className='gap-2 justify-between items-center'>
              <div className='flex items-center gap-2'>
                <FileText width={14} />
                {t('header.applications')}
                {Object.keys(applicationSummary ?? {}).length > 0 ? (
                  <span className='text-muted-foreground text-xs'>
                    ({Object.keys(applicationSummary ?? {}).length ?? 0})
                  </span>
                ) : null}
              </div>
              {unreadIndicator}
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>
            <div className='flex justify-between gap-2'>
              <span>{t('shared.premium')}</span>
              <PremiumModal>
                <Button variant='outline' size='xs' className='text-xs'>
                  {t(
                    subscriptionActive
                      ? 'premium.change-plan'
                      : 'shared.subscribe',
                  )}
                </Button>
              </PremiumModal>
            </div>

            <div className='mt-1'>
              <span className={`font-normal ${subscriptionColor.text}`}>
                {subscriptionName === 'Free'
                  ? t('header.not-subscribed')
                  : subscriptionName}
              </span>
            </div>
          </DropdownMenuLabel>

          {hideCharacterContext || hasFixedTheme ? null : (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                <span className='flex items-center justify-between'>
                  <span>{t('header.change-theme')}</span>
                  <span className='opacity-70 text-[0.7rem] mr-0.5 font-normal'>
                    {t(`header.${theme}`)}
                  </span>
                </span>
                <div className='flex gap-1 items-center'>
                  {(['light', 'dark', 'system'] as const).map((option) => {
                    const Icon = {
                      light: Sun,
                      dark: Moon,
                      system: Monitor,
                    }[option]
                    return (
                      <div
                        key={option}
                        onClick={() => setTheme(option)}
                        className={`mt-1.5 cursor-pointer grow ${buttonVariants(
                          {
                            variant: option === theme ? 'default' : 'secondary',
                            size: 'sm',
                          },
                        )}`}
                      >
                        <Icon width={18} />
                      </div>
                    )
                  })}
                </div>
              </DropdownMenuLabel>
            </>
          )}

          <DropdownMenuSeparator />

          {impersonating ? (
            <button onClick={stopImpersonation} className='w-full'>
              <DropdownMenuItem className='flex gap-2 items-center'>
                <VenetianMask width={14} />
                Stop impersonating
              </DropdownMenuItem>
            </button>
          ) : null}

          <a href='/logout'>
            <DropdownMenuItem className='flex gap-2 items-center'>
              <LogOut width={14} />
              {t('header.log-out')}
            </DropdownMenuItem>
          </a>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export default HeaderDropdown
